import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import VoloceanLoader from '../loading/VoloceanLoader';
import toast from 'react-hot-toast';
import { isAdminRole } from '../../utils/authRoles';

export interface ClientSpecificRouteProps {
  children: React.ReactNode;
  allowedEmails?: string[];
  allowedDomains?: string[];
  allowedClientIds?: string[];
}

export const ClientSpecificRoute: React.FC<ClientSpecificRouteProps> = ({
  children,
  allowedEmails = [],
  allowedDomains = [],
  allowedClientIds = [],
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <VoloceanLoader size="lg" message="Verifying access..." />
      </div>
    );
  }

  // Not logged in at all -> send to auth with redirect
  if (!user) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  // Admins always have access
  if (isAdminRole(user.role)) {
    return <>{children}</>;
  }

  // Verify client access
  if (user.role === 'client') {
    const userEmail = user.email?.toLowerCase() || '';
    const userId = user.uid;
    const clientId = user.clientId || '';

    const isEmailAllowed = allowedEmails.map(e => e.toLowerCase()).includes(userEmail);
    const isDomainAllowed = allowedDomains.some(domain => userEmail.endsWith(domain.toLowerCase()));
    const isClientIdAllowed = allowedClientIds.includes(clientId) || allowedClientIds.includes(userId);

    if (isEmailAllowed || isDomainAllowed || isClientIdAllowed) {
      return <>{children}</>;
    }
  }

  // Unauthorized client or other role
  toast.error('You do not have permission to view this page.', { id: 'unauthorized-client-route' });
  
  if (user.role === 'client') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/auth" replace />;
};
